import { useState } from "react";
import { FileText, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import {
  setComplianceContext,
  validateInvoice,
} from "../../services/compliance-api";
import type { ValidationResponse } from "../../types/compliance";

interface Props {
  workflowId: string;
  tenantId?: string;
  onComplete?: (result: ValidationResponse) => void;
}

const CURRENCIES = ["EUR", "USD", "GBP", "CHF"];

export default function InvoiceValidationPanel({
  workflowId,
  tenantId = "default",
  onComplete,
}: Props) {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("1000");
  const [currency, setCurrency] = useState("EUR");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ValidationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleValidate() {
    if (!invoiceNumber.trim()) {
      setError("Invoice number is required.");
      return;
    }
    const amount = parseFloat(invoiceAmount);
    if (isNaN(amount) || amount < 0) {
      setError("Please enter a valid invoice amount.");
      return;
    }
    setComplianceContext(tenantId, "FINANCE_REVIEWER");
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await validateInvoice({
        tenant_id: tenantId,
        workflow_id: workflowId,
        invoice_number: invoiceNumber.trim(),
        invoice_amount: amount,
        currency,
      });
      setResult(res);
      onComplete?.(res);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Invoice validation failed",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <FileText size={14} className="text-amber-600" />
        Invoice Validation
      </h3>

      <div className="space-y-2">
        <input
          type="text"
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          placeholder="INV-001"
          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-amber-500"
        />
        <div className="flex gap-2">
          <input
            type="number"
            value={invoiceAmount}
            onChange={(e) => setInvoiceAmount(e.target.value)}
            placeholder="Amount"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-amber-500"
          />
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-amber-500"
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => void handleValidate()}
          disabled={loading}
          className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white text-sm py-1.5 rounded-lg flex items-center justify-center gap-2 transition"
        >
          {loading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <FileText size={14} />
          )}
          {loading ? "Validating…" : "Validate Invoice"}
        </button>

        {error && (
          <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1.5">
            <AlertCircle size={12} className="shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {result && (
          <div
            className={`text-xs px-3 py-2 rounded-lg border ${
              result.passed
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              {result.passed ? (
                <CheckCircle size={12} />
              ) : (
                <AlertCircle size={12} />
              )}
              <span className="font-semibold">
                {result.passed ? "Passed" : "Failed"}
              </span>
              <span className="ml-auto font-mono">
                {(result.score * 100).toFixed(0)}%
              </span>
            </div>
            <p>{result.message}</p>
            {result.findings.length > 0 && (
              <ul className="list-disc list-inside mt-1 opacity-80">
                {result.findings.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            )}
            {result.metadata && Object.keys(result.metadata).length > 0 && (
              <div className="mt-1 pt-1 border-t border-red-200 opacity-70">
                {Object.entries(result.metadata).map(([k, v]) => (
                  <span key={k} className="mr-2">
                    {k}: {String(v).slice(0, 30)}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
